import { Box, Stack } from '@mui/material';
import { first } from 'lodash';
import { FieldValues, useWatch } from 'react-hook-form';

import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { FieldNameFromFormShape } from '@/types/react-hook-form';

import { getProviderFromIntegrationClientKind } from '../assetProviderIntegrations.utils';
import { AssetIntegrationProviders } from './constants';
import {
  useAssetProviderDataLinkingState,
  useAssetProviderDisplayName,
} from './hooks/useEnabledAssetsIntegrations';
import {
  IntegrationEntitiesTypeahead,
  IntegrationEntitiesTypeaheadProps,
} from './IntegrationEntitiesTypeahead/IntegrationEntitiesTypeahead';
import { IntegrationInputCard } from './IntegrationInputCard';

export type SharedIntegrationEntityTypeaheadProps<
  FormShape extends FieldValues,
> = Omit<
  IntegrationEntitiesTypeaheadProps<FormShape>,
  // These properties are all controlled internally by this component
  'provider' | 'allowAllEntities' | 'notLoggedIn' | 'label' | 'hideLabel'
> & {
  // includeAllEntitiesFieldName is the field name for the switch that controls whether
  // the user can select from entities for any integration_client rather than being limited to the
  // integration_clients that are connected to the household. This is only exposed for Addepar as of
  // 12/2024.
  includeAllEntitiesFieldName: FieldNameFromFormShape<FormShape>;
  // If true, the component will only render the typeahead input and not the integration input card or the
  // switch for selecting all entities.
  inputOnly?: boolean;
  // Optional callback to determine if an option should be disabled
  getOptionDisabled?: (optionValue: string) => boolean;
};

/**
 * @description SharedIntegrationEntityTypeahead is a component that allows the user to select integration_entities
 * for a given integration_client. It's the canonical component that should be used for all asset provider integrations.
 */
export function SharedIntegrationEntityTypeahead<
  FormShape extends FieldValues,
>({
  includeAllEntitiesFieldName,
  control,
  inputOnly,
  getOptionDisabled,
  ...typeaheadProps
}: SharedIntegrationEntityTypeaheadProps<FormShape>) {
  const { householdIntegrationLink } = useHouseholdDetailsContext();
  const { canConnectToIntegrations, integrationsAvailableForDataLinking } =
    useAssetProviderDataLinkingState();
  const assetProviderName = useAssetProviderDisplayName({ isInline: true });
  const allowAllEntities = useWatch({
    control,
    name: includeAllEntitiesFieldName,
  });

  // If there are no enabled integrations, don't show this control.
  // The check for assetProviderName is really just for TS, since it'll be present
  // if there are enabled integrations.
  if (canConnectToIntegrations.length === 0 || !assetProviderName) {
    return null;
  }

  // Okay to assert here because of the check above.
  const connectableIntegration = first(canConnectToIntegrations)!;
  const primaryIntegration =
    (householdIntegrationLink &&
      getProviderFromIntegrationClientKind(householdIntegrationLink)) ||
    connectableIntegration;
  const hasIntegrationDataAccess =
    primaryIntegration &&
    integrationsAvailableForDataLinking.includes(primaryIntegration);
  return inputOnly ? (
    <Box width="100%">
      <IntegrationEntitiesTypeahead<FormShape>
        provider={primaryIntegration}
        notLoggedIn={!hasIntegrationDataAccess}
        allowAllEntities={false}
        control={control}
        label={`Associated ${assetProviderName} accounts or portfolio groups`}
        hideLabel
        getOptionDisabled={getOptionDisabled}
        {...typeaheadProps}
      />
    </Box>
  ) : (
    <IntegrationInputCard providerName={assetProviderName}>
      <Box width="100%">
        <Stack spacing={2}>
          <IntegrationEntitiesTypeahead<FormShape>
            provider={primaryIntegration}
            notLoggedIn={!hasIntegrationDataAccess}
            allowAllEntities={allowAllEntities}
            control={control}
            label={`Associated ${assetProviderName} accounts or portfolio groups`}
            hideLabel
            getOptionDisabled={getOptionDisabled}
            {...typeaheadProps}
          />
          {primaryIntegration === AssetIntegrationProviders.ADDEPAR &&
            includeAllEntitiesFieldName && (
              <FormAwareSwitch
                labelPosition="right"
                control={control}
                disabled={!hasIntegrationDataAccess}
                label="Select from all entities across Addepar"
                fieldName={includeAllEntitiesFieldName}
              />
            )}
        </Stack>
      </Box>
    </IntegrationInputCard>
  );
}
